/**
 * Pincode-based Estimated Delivery Date widget
 * Protecta / Turbo theme
 *
 * Data sources (static JSON, theme assets, no backend required):
 *   - bluedart-tat.json     { "pincode": transitDays, ... }   ~21.3k entries
 *   - valid-pincodes.json   [ "pincode", ... ]                ~19.6k entries
 *
 * Logic:
 *   1. Pincode found in bluedart-tat.json  -> real "Get it delivered by <date>"
 *   2. Pincode found in valid-pincodes.json (but not Bluedart-served)
 *                                            -> flat "Estimated delivery: 7-10 business days" (India Post)
 *   3. Pincode not found in either           -> "Please enter a valid pincode"
 *
 * Dispatch cutoff rules (confirmed by PB):
 *   Mon-Sat before 13:00  -> dispatch today
 *   Mon-Fri after 13:00   -> dispatch next day
 *   Sat after 13:00       -> dispatch next Monday
 *   Sun (all day)         -> dispatch next Monday
 *
 * Delivery day adjustment:
 *   Bluedart doesn't deliver on Sundays for most pincodes, so if the
 *   calculated delivery date lands on a Sunday, it's bumped to Monday.
 *
 * NOTE: Bluedart TAT is treated as calendar days (not business days), matching
 * how it's expressed in the source Excel. If Bluedart doesn't deliver on Sundays
 * in practice, this will need a small adjustment - flag if that's the case.
 */

(function () {
  'use strict';

  var CONFIG = {
    // Update these to match your actual theme asset URLs, e.g. via Liquid:
    // data-tat-url="{{ 'bluedart-tat.json' | asset_url }}"
    tatUrl: null,
    validPincodesUrl: null,
    dispatchCutoffHour: 13, // 1 PM, 24hr format, store's local time
    fallbackDeliveryText: '7-10 business days',
    sessionStorageKeys: {
      tat: 'protecta_bluedart_tat_v1',
      valid: 'protecta_valid_pincodes_v1'
    }
  };

  var MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ---------- Date helpers ----------

  function addDays(date, days) {
    var d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  function ordinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  /**
   * Bluedart generally doesn't deliver on Sundays. If a calculated
   * delivery date lands on a Sunday, bump it to the following Monday.
   */
  function bumpIfSunday(date) {
    if (date.getDay() === 0) {
      return addDays(date, 1);
    }
    return date;
  }

  function formatDisplayDate(date) {
    var day = date.getDate();
    var month = MONTH_NAMES[date.getMonth()];
    return day + ordinalSuffix(day) + ' ' + month;
  }

  /**
   * Determines the dispatch date given an order timestamp.
   * Mon-Sat before cutoff -> today. Mon-Fri after cutoff -> next day.
   * Sat after cutoff, and all Sunday -> Monday.
   */
  function getDispatchDate(now) {
    var day = now.getDay(); // 0 = Sun, 1 = Mon, ... 6 = Sat
    var hour = now.getHours() + now.getMinutes() / 60;
    var beforeCutoff = hour < CONFIG.dispatchCutoffHour;

    if (day === 0) {
      // Sunday -> next Monday
      return addDays(now, 1);
    }

    if (day === 6) {
      // Saturday
      return beforeCutoff ? now : addDays(now, 2); // after cutoff -> Monday
    }

    // Mon-Fri
    return beforeCutoff ? now : addDays(now, 1);
  }

  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /**
   * Human-readable dispatch label, e.g. "Ships Today" / "Ships Monday".
   * Always names the explicit day rather than mixing in "Tomorrow" -
   * keeps weekend rollover cases unambiguous.
   */
  function getDispatchLabel(now) {
    var dispatchDate = getDispatchDate(now);
    var isSameDay = dispatchDate.toDateString() === now.toDateString();
    if (isSameDay) return 'Ships Today';
    return 'Ships ' + DAY_NAMES[dispatchDate.getDay()];
  }

  function loadJSON(url, cacheKey) {
    var cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        return Promise.resolve(JSON.parse(cached));
      } catch (e) {
        sessionStorage.removeItem(cacheKey);
      }
    }
    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to load ' + url);
        return res.json();
      })
      .then(function (data) {
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
        } catch (e) {
          // sessionStorage full or unavailable - fine, just won't cache
        }
        return data;
      });
  }

  function loadDeliveryData() {
    return Promise.all([
      loadJSON(CONFIG.tatUrl, CONFIG.sessionStorageKeys.tat),
      loadJSON(CONFIG.validPincodesUrl, CONFIG.sessionStorageKeys.valid)
    ]).then(function (results) {
      var tatMap = results[0];
      var validList = results[1];
      // Convert valid-pincodes array to a Set for O(1) lookup
      var validSet = new Set(validList);
      return { tatMap: tatMap, validSet: validSet };
    });
  }

  // ---------- Core lookup logic ----------

  function isValidFormat(pincode) {
    return /^[1-9][0-9]{5}$/.test(pincode);
  }

  /**
   * Returns one of:
   *   { status: 'serviceable', date: Date }
   *   { status: 'fallback' }              // valid pincode, not Bluedart-served
   *   { status: 'invalid' }               // not a real pincode
   */
  function resolveDelivery(pincode, data, now) {
    if (!isValidFormat(pincode)) {
      return { status: 'invalid' };
    }

    if (Object.prototype.hasOwnProperty.call(data.tatMap, pincode)) {
      var transitDays = data.tatMap[pincode];
      var dispatchDate = getDispatchDate(now);
      var deliveryDate = addDays(dispatchDate, transitDays);
      deliveryDate = bumpIfSunday(deliveryDate);
      return { status: 'serviceable', date: deliveryDate };
    }

    if (data.validSet.has(pincode)) {
      return { status: 'fallback' };
    }

    return { status: 'invalid' };
  }

  // ---------- Widget wiring ----------

  function initPincodeWidget(root) {
    var input = root.querySelector('[data-pincode-input]');
    var button = root.querySelector('[data-pincode-check]');
    var resultEl = root.querySelector('[data-pincode-result]');
    var badgeEl = root.querySelector('[data-pincode-badge]');

    if (!input || !button || !resultEl) return;

    // Render the "Ships Today" / "Ships Monday" status pill immediately -
    // this only needs the current time, not the pincode data fetch.
    if (badgeEl) {
      badgeEl.textContent = getDispatchLabel(new Date());
    }

    // Avoid double-binding if this runs again after a section re-render
    if (button.dataset.pincodeBound === 'true') return;
    button.dataset.pincodeBound = 'true';

    var dataPromise = null;
    function getData() {
      if (!dataPromise) dataPromise = loadDeliveryData();
      return dataPromise;
    }

    function setResult(html, variant) {
      resultEl.innerHTML = html;
      resultEl.classList.remove('pincode-widget__result--error', 'pincode-widget__result--muted');
      if (variant === 'error') {
        resultEl.classList.add('pincode-widget__result--error');
      } else if (variant === 'muted') {
        resultEl.classList.add('pincode-widget__result--muted');
      }
      input.classList.toggle('pincode-widget__input--error', variant === 'error');
    }

    function handleCheck() {
      var pincode = (input.value || '').trim();

      if (!isValidFormat(pincode)) {
        setResult('Please enter a valid 6-digit pincode', 'error');
        return;
      }

      button.disabled = true;
      setResult('Checking...', 'muted');

      getData()
        .then(function (data) {
          var now = new Date();
          var result = resolveDelivery(pincode, data, now);

          if (result.status === 'serviceable') {
            setResult('Get it delivered by <strong>' + formatDisplayDate(result.date) + '</strong>', null);
          } else if (result.status === 'fallback') {
            setResult('Estimated delivery: ' + CONFIG.fallbackDeliveryText, null);
          } else {
            setResult('Please enter a valid pincode', 'error');
          }
        })
        .catch(function () {
          setResult('Could not check delivery date. Please try again.', 'error');
        })
        .finally(function () {
          button.disabled = false;
        });
    }

    var row = root.querySelector('.pincode-widget__row') || root;

    function updateReadyState() {
      var isReady = /^[0-9]{6}$/.test((input.value || '').trim());
      input.classList.toggle('is-ready', isReady);
      row.classList.toggle('is-ready', isReady);
    }

    // Re-check on every keystroke, paste, or programmatic value change -
    // this is what makes the button revert the instant the 6th digit
    // is deleted, not just on blur/submit.
    input.addEventListener('input', updateReadyState);
    updateReadyState(); // handles a pre-filled value on load, if any

    button.addEventListener('click', handleCheck);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCheck();
      }
    });

    // Prefetch data in the background once the user focuses the field,
    // so the actual "Check" click feels instant.
    input.addEventListener('focus', function prefetch() {
      getData();
      input.removeEventListener('focus', prefetch);
    }, { once: true });
  }

  function initAll() {
    var widgets = document.querySelectorAll('[data-pincode-widget]');
    widgets.forEach(function (root) {
      // Read config from data attributes set in Liquid, e.g.:
      // <div data-pincode-widget data-tat-url="{{ 'bluedart-tat.json' | asset_url }}"
      //      data-valid-url="{{ 'valid-pincodes.json' | asset_url }}">
      if (!CONFIG.tatUrl) CONFIG.tatUrl = root.dataset.tatUrl;
      if (!CONFIG.validPincodesUrl) CONFIG.validPincodesUrl = root.dataset.validUrl;
      initPincodeWidget(root);
    });
  }

  // Initial load
  document.addEventListener('DOMContentLoaded', initAll);

  // Re-init after Shopify section re-renders (e.g. sections-swap.js on variant change)
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('section:rendered', initAll); // adjust to your theme's actual event name if different

  // Expose for manual re-init if needed elsewhere in the theme
  window.ProtectaPincodeWidget = { init: initAll };
})();